import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

// Real API statuses from PRODUCTS-API-DOC.md
// status values: "pending", "paid", "cancelled", "delivered", "expired"
const STATUS_LABELS = {
  pending: 'En attente',
  paid: 'Payé',
  cancelled: 'Annulé',
  delivered: 'Livré',
  expired: 'Expiré',
}

export default {
  url(payload) {
    const { userId, status } = payload || {}
    return buildApiUrl(ADMIN_ENDPOINTS.PRODUCT_PURCHASES, {
      queryParams: {
        userId: userId || undefined,
        status: status || undefined,
        limit: 200,
      }
    })
  },

  parse(raw = {}, payload = {}) {
    if (raw.statusCode !== 200) {
      return {
        statusCode: raw?.statusCode || 400,
        message: raw?.message || 'Failed to fetch product purchases',
        data: null,
      }
    }

    // /product/list/buyings returns { statusCode: 200, count: N, data: [...] }
    const purchases = Array.isArray(raw.data) ? raw.data : []
    const total = raw.count || purchases.length

    // Use products map if passed through context (set from parent via @fetched)
    const productsMap = payload?._productsMap || {}

    const formatted = purchases.map(p => {
      const product = productsMap[p.productId] || {}
      const productName = product.name || p.productName || `Produit #${p.productId}` || '—'

      return {
        id: p.id,
        // Buyer info - API only gives userId, not name
        buyerId: p.userId,
        buyerName: p.userName || p.buyerName || `User ${(p.userId || '').slice(0, 8)}`,
        buyerType: p.buyerType || (product.target === 'rider' ? 'rider' : 'restaurant'),
        phone: p.phone || '',
        // Product
        productId: p.productId,
        productName,
        quantity: p.quantity ?? 1,
        unitPrice: p.unitPrice ?? 0,
        totalPrice: p.totalPrice ?? 0,
        currency: p.currency || 'XOF',
        // Status (real API values)
        status: p.status || 'pending',
        statusLabel: STATUS_LABELS[p.status] || p.status || '—',
        // Payment
        paymentMethod: p.paymentMethod || '',
        paymentMethodLabel: p.paymentMethod || '—',
        paymentId: p.paymentId || '',
        // Expiry
        expiresAt: p.expiresAt,
        // Meta
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      }
    })

    let finalData = formatted

    // === Recherche textuelle locale ===
    const searchTerm = (payload?.search || '').toString().trim().toLowerCase()
    if (searchTerm) {
      finalData = finalData.filter(el => {
        return (el.buyerName && el.buyerName.toLowerCase().includes(searchTerm)) ||
               (el.productName && el.productName.toLowerCase().includes(searchTerm)) ||
               (el.id && el.id.toLowerCase().includes(searchTerm)) ||
               (el.status && el.status.toLowerCase().includes(searchTerm))
      })
    }

    // Filter by status
    const statusFilter = payload?.status
    if (statusFilter) {
      finalData = finalData.filter(el => el.status === statusFilter)
    }

    // Filter by buyerType (rider/restaurant) — passed from TableList colFilters
    const buyerTypeFilter = payload?.buyerType
    if (buyerTypeFilter) {
      finalData = finalData.filter(el => el.buyerType === buyerTypeFilter)
    }

    // === Tri dynamique ===
    const { sortKey, sortOrder } = payload || {}
    if (sortKey) {
      const order = sortOrder === 'desc' ? -1 : 1
      finalData = [...finalData].sort((a, b) => {
        const valA = a?.[sortKey]
        const valB = b?.[sortKey]
        if (typeof valA === 'string' && typeof valB === 'string') return valA.localeCompare(valB) * order
        if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * order
        return 0
      })
    }

    // Sort by date desc by default
    if (!sortKey) {
      finalData = [...finalData].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    }

    // === Pagination locale ===
    const { page = 0, limit = 20 } = payload || {}
    const paginated = finalData.slice(page * limit, (page + 1) * limit)

    return {
      statusCode: 200,
      message: 'Ok',
      data: {
        total: searchTerm ? finalData.length : total,
        result: paginated,
      },
    }
  },
}
